import { DestroyRef, inject, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ShowsItemRef, Translated } from './shows.model';
import { ShowsService } from './shows.service';

/**
 * A page's item in the reader's language, as the news page does it: when the item arrives in
 * English because the requested language is `absent` or `failed`, ask for it, show the English
 * with a spinner while the answer says it is worth waiting, and swap the text in once it settles.
 * A `partial` or `refused` translation is not asked for again.
 *
 * Create one per page, in an injection context; it stops when the page is destroyed.
 */
export class ShowsTranslationFollower<T extends Translated> {
  private service = inject(ShowsService);
  private subscription?: Subscription;

  /** A translation has been asked for and has not settled. */
  readonly translating = signal(false);

  constructor() {
    inject(DestroyRef).onDestroy(() => this.stop());
  }

  /**
   * @param show called with each newer version of the item. A translation's answer carries no
   * related records, so `show` should keep the ones the page has.
   */
  follow(ref: ShowsItemRef, item: T, show: (item: T) => void): void {
    this.stop();

    if (ShowsService.needsTranslation(item) && item.requestedLocale) {
      this.translating.set(true);
      this.subscription = this.service.requestTranslation<T>(ref, item.requestedLocale).subscribe({
        next: (answer) => this.watch(ref, answer, show, true),
        error: () => this.translating.set(false),
      });
    } else {
      this.watch(ref, item, show, false);
    }
  }

  stop(): void {
    this.subscription?.unsubscribe();
    this.translating.set(false);
  }

  private watch(ref: ShowsItemRef, item: T, show: (item: T) => void, showIt: boolean): void {
    if (showIt) {
      show(item);
    }

    if (!item.worthPolling) {
      this.translating.set(false);
      return;
    }

    this.translating.set(true);
    this.subscription = this.service
      .watchTranslation<T>(ref)
      .pipe(finalize(() => this.translating.set(false)))
      .subscribe((settled) => show(settled));
  }
}
